import React, { useState } from 'react';
import { FaChartPie, FaSearch } from "react-icons/fa";
import { Bar, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from 'chart.js';
import CountryList from "../CountryList";
import { googleApi } from "../../services/api";
import './CompetitorAnalysis.css';  // Import the CSS file

// Registering Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const CompetitorAnalysis = () => {
    const [keyword, setKeyword] = useState('');
    const [country, setCountry] = useState('');
    const [results, setResults] = useState([]);
    const [, setError] = useState('');
    const [loading, setLoading] = useState(false);

const stopWords = ['a', 'an', 'the', 'i', 'me', 'my', 'mine', 'you', 'your', 'yours', 'he', 'him', 'his', 'she', 'her', 'hers', 'it', 'its', 'we', 'us', 'our', 'ours', 'they', 'them', 'their', 'theirs', 'who', 'whom', 'whose', 'and', 'but', 'or', 'nor', 'for', 'so', 'yet', 'either', 'neither', 'both', 'than', 'that', 'as', 'while', 'although', 'because', 'if', 'unless', 'whether', 'in', 'on', 'at', 'to', 'of', 'from', 'by', 'about', 'as', 'with', 'between', 'over', 'under', 'above', 'below', 'across', 'through', 'during', 'before', 'after', 'against', 'without', 'within', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'shall', 'can', 'could', 'may', 'might', 'must', 'ought', 'need', 'very', 'too', 'just', 'only', 'also', 'even', 'still', 'yet', 'then', 'now', 'here', 'there', 'back', 'forward', 'further', 'more', 'less', 'once', 'again', 'oh', 'ah', 'uh', 'hmm', 'wow', 'yay', 'oops', 'hey', 'alas', 'some', 'any', 'no', 'none', 'all', 'most', 'few', 'several', 'many', 'much', 'each', 'every', 'either', 'neither', 'a lot', 'this', 'that', 'these', 'those', 'which', 'what', 'where', 'when', 'how', 'why', 'what', 'how', 'whoever', 'whatever', 'whenever', 'wherever', 'whichever', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty', 'there', 'here', 'back', 'up', 'down', 'out', 'in', 'off', 'over', 'under', 'along', 'despite', 'since', 'let', 'us', 'let’s', 'got', 'get', 'gotta', 'gonna', 'wanna', 'have to', 'used to', 'be able to', 'he\'s', 'she\'s', 'it\'s', 'they\'re', 'we\'re', 'you\'re', 'who\'s', 'what\'s', 'where\'s', 'when\'s', 'how\'s', 'let\'s', 'ain\'t', 'won\'t', 'can\'t', 'doesn\'t', 'don\'t', 'didn\'t', 'even', 'just', 'only', 'merely', 'simply', 'actually', 'really', 'truly', 'especially', 'particularly'];

    const extractKeywords = (description) => {
        const words = description.toLowerCase().split(/\s+/);
        const keywords = words.filter(word => word.length > 2 && !stopWords.includes(word));
        const keywordFrequency = {};
        keywords.forEach(word => {
            keywordFrequency[word] = (keywordFrequency[word] || 0) + 1;
        });
        return Object.keys(keywordFrequency)
            .sort((a, b) => keywordFrequency[b] - keywordFrequency[a])
            .slice(0, 5);
    };

    const handleSearch = async () => {
        setError('');
        if (!keyword || !country) {
            setError('Please fill in all fields.');
            return;
        }

        setLoading(true);
        try {
            const response = await googleApi.post("keyword-search/", { keyword, country });
            const formattedResults = response.data.map(app => {
                const extractedKeywords = extractKeywords(app.description || '');
                return {
                    appId: app.appId || 'N/A',
                    title: app.title || 'N/A',
                    screenshot: app.icon || 'N/A',
                    keywords: extractedKeywords.length ? extractedKeywords : ['N/A'],
                    installs: app.installs !== undefined ? app.installs : 'N/A',
                    score: app.score !== undefined ? app.score : 'N/A',
                    free: app.free !== undefined ? app.free : 'N/A',
                    category: app.genre || 'N/A',
                    developer: app.developer || 'N/A',
                    currency: app.currency || 'N/A',
                    last_updated: app.last_updated || 'N/A',
                    app_store_link: app.appId ? `https://play.google.com/store/apps/details?id=${app.appId}` : '#',
                };
            });
            setResults(formattedResults);
        } catch (error) {
            console.error('Error fetching search results:', error);
            setError(error.response ? 'Failed to fetch data. Please try again.' : 'Failed to make request.');
        } finally {
            setLoading(false);
        }
    };
const normalizeInstalls = (installs) => {
    if (typeof installs === 'string') {
        installs = installs.toLowerCase().replace(/[,+]/g, ''); // Remove commas and plus signs
        if (installs.includes('k')) {
            return parseFloat(installs) * 1000; // Convert "k" (thousands) to actual numbers
        } else if (installs.includes('m')) {
            return parseFloat(installs) * 1000000; // Convert "m" (millions) to actual numbers
        } else if (installs.includes('b')) {
            return parseFloat(installs) * 1000000000; // Convert "b" (billions) to actual numbers
        } else if (installs.includes('t')) {
            return parseFloat(installs) * 1000000000000; // Convert "t" (trillions) to actual numbers
        }
    }
    return parseFloat(installs) || 0; // Default case to parse regular numbers
};

// Chart for Installs (after normalizing the data)
const installsData = {
    labels: results.map(app => app.title),
    datasets: [
        {
            label: 'Installs',
            data: results.map(app => normalizeInstalls(app.installs)),
            backgroundColor: "#8ecae6",
        },
    ],
};

const installsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: 'top',
        },
        title: {
            display: true,
            text: 'Competitor App Installs',
        },
    },
    scales: {
        y: {
            beginAtZero: true,
            ticks: {
                callback: function(value) {
                    return value.toLocaleString(); // Format the numbers with commas for readability
                },
            },
        },
    },
};

    // Chart for Scores
    const scoreData = {
        labels: results.map(app => app.title),
        datasets: [
            {
                label: 'Score',
                data: results.map(app => app.score !== 'N/A' ? app.score : 0),
                backgroundColor: "#8ecae6",
            },
        ],
    };

    const scoreOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Competitor App Scores',
            },
        },
    };
    const categoryData = {
        labels: Array.from(new Set(results.map(app => app.category))),
        datasets: [{
            label: 'App Categories',
            data: Array.from(new Set(results.map(app => app.category))).map(category =>
                results.filter(app => app.category === category).length
            ),
            backgroundColor: "#8ecae6",
        }],
    };

    const categoryOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'App Category Distribution',
            },
        },
    };

    const doughnutData = {
      labels: ["High Score", "Low Score"],
      datasets: [
        {
          label: "Score Distribution",
          data: [
            results.filter((app) => app.score > 4).length,
            results.filter((app) => app.score <= 4).length,
          ],
          backgroundColor: ["#8ecae6", "#253666"],
        },
      ],
    };

    const doughnutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Score Distribution',
            },
        },
    };

    return (
      <div className="container mt-4">
        <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <FaChartPie className="mr-3 text-purple-500" />
            Competitor Analysis
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                <div className="md:col-span-5">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Enter App Name</label>
                    <input
                    type="text"
                    placeholder="e.g. Instagram"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm text-gray-700 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition duration-200"
                    />
                </div>
                <div className="md:col-span-5">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Country</label>
                    <CountryList
                    onSelect={(selectedCountry) => setCountry(selectedCountry)}
                    />
                </div>
                <div className="md:col-span-2">
                    <button
                    onClick={handleSearch}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-md shadow transition duration-200 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                    disabled={loading}
                    >
                    {loading ? (
                        <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    ) : (
                        <FaSearch className="mr-2" />
                    )}
                    {loading ? "Loading..." : "Search"}
                    </button>
                </div>
            </div>
        </div>
        <div className="mt-4">
 
  <h4 className='' style={{ fontSize: "1rem", marginBottom: "1.5rem", fontWeight: "700", display:"inline-block", border: "1px solid #023047",background:"white", borderRadius:"10px", padding: "10px" }}>
    Competitor Analysis - Search Results
  </h4>
  <div className="row">
    {results.map((app) => (
      <div className="col-lg-3 col-md-4 col-sm-6 mb-4" key={app.appId}>
        <div
          className="card h-100"
          style={{
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
            transition: "transform 0.3s, box-shadow 0.3s",
            backgroundColor: "#f8f9fa",
          }}
        >
          {/* Card Header */}
          <div
            className="card-header text-center"
            style={{
              backgroundColor: "#253666",
              color: "#fff",
              fontSize: "1rem",
              fontWeight: "700",
              padding: "10px",
            }}
          >
            {app.title}
          </div>

          {/* Card Body */}
          <div className="card-body d-flex flex-column align-items-center text-center">
            <img
              src={app.screenshot}
              alt={app.title}
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                marginBottom: "15px",
                border: "3px solid #253666",
                objectFit: "cover",
              }}
            />
            <span
              className="badge"
              style={{
                fontSize: "0.8rem",
                padding: "5px 10px",
                backgroundColor: "#8ecae6",
                color: "#253666",
                borderRadius: "10px",
                marginBottom: "10px",
              }}
            >
              {app.category}
            </span>
            <p
              className="text-muted mb-1"
              style={{ fontSize: "0.85rem", fontWeight: "500" }}
            >
              Developer: {app.developer}
            </p>
            <p
              className="text-muted mb-1"
              style={{ fontSize: "0.85rem", fontWeight: "500" }}
            >
              Installs: {app.installs}
            </p>
            <p
              className="text-muted mb-1"
              style={{ fontSize: "0.85rem", fontWeight: "500" }}
            >
              Score: {app.score}
            </p>
            <a
              href={app.app_store_link}
              target="_blank"
              rel="noopener noreferrer"
              className="btn"
              style={{
                fontSize: "0.8rem",
                padding: "8px 20px",
                borderRadius: "20px",
                backgroundColor: "#253666",
                color: "#fff",
                transition: "all 0.3s",
              }}
            >
              View App
            </a>
          </div>
        </div>
      </div>
    ))}
  </div>
</div>


        <div className="row">
  <div className="col-md-6">
    <div className="card" style={{ borderRadius: "15px", padding: "20px", margin: "15px", backgroundColor: "#fff",  boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
            transition: "transform 0.3s, box-shadow 0.3s", }}>
      <div className="card-body" style={{ height: "400px" }}>
        <Bar data={installsData} options={installsOptions} />
      </div>
    </div>
  </div>
  <div className="col-md-6">
    <div className="card" style={{ borderRadius: "15px", padding: "20px", margin: "15px", backgroundColor: "#fff",  boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
            transition: "transform 0.3s, box-shadow 0.3s", }}>
      <div className="card-body" style={{ height: "400px" }}>
        <Bar data={scoreData} options={scoreOptions} />
      </div>
    </div>
  </div>
</div>

<div className="row mt-4">
  <div className="col-md-6">
    <div className="card" style={{ borderRadius: "15px", padding: "20px", margin: "15px", backgroundColor: "#fff",  boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
            transition: "transform 0.3s, box-shadow 0.3s", }}>
      <div className="card-body" style={{ height: "400px" }}>
        <Bar data={categoryData} options={categoryOptions} />
      </div>
    </div>
  </div>
  <div className="col-md-6">
    <div className="card" style={{ borderRadius: "15px", padding: "20px", margin: "15px", backgroundColor: "#fff",  boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
            transition: "transform 0.3s, box-shadow 0.3s", }}>
      <div className="card-body" style={{ height: "400px" }}>
        <Doughnut data={doughnutData} options={doughnutOptions} />
      </div>
    </div>
  </div>
</div>


      </div>
    );
};

export default CompetitorAnalysis;